/**
 * Utility for managing pending HR test state in localStorage.
 * Used when a job seeker comes from a shared test link and needs to complete
 * signup/login before taking the HR test.
 *
 * HR-shared tests bypass skill verification requirement - jobseekers can take
 * these tests directly without being verified.
 */

const PENDING_TEST_KEY = 'vetriaai_pending_test';

/**
 * Check if localStorage is available
 */
const isStorageAvailable = () => {
  try {
    if (typeof window === 'undefined') {
      return false;
    }
    if (typeof Storage === 'undefined' || typeof localStorage === 'undefined') {
      return false;
    }
    // Try to access localStorage - some browsers throw errors in certain contexts
    const test = '__storage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch (e) {
    // Storage access denied or not available
    console.warn('localStorage is not available:', e.message);
    return false;
  }
};

/**
 * Save pending test ID when user comes from a shared test link
 * bypassVerification is always true for HR-shared links
 */
export const savePendingTest = (testId, testTitle, hrEmail) => {
  if (!isStorageAvailable()) {
    console.warn('localStorage is not available, cannot save pending test');
    return;
  }
  const data = {
    testId,
    testTitle,
    hrEmail,
    savedAt: Date.now(),
    bypassVerification: true, // HR-shared tests always bypass verification
  };
  try {
    localStorage.setItem(PENDING_TEST_KEY, JSON.stringify(data));
    console.log('💾 Pending test saved to localStorage:', data);
  } catch (error) {
    console.error('Error saving pending test to localStorage:', error);
  }
};

/**
 * Get the pending test data if it exists
 */
export const getPendingTest = () => {
  if (!isStorageAvailable()) {
    console.warn('localStorage is not available, cannot get pending test');
    return null;
  }
  try {
    const data = localStorage.getItem(PENDING_TEST_KEY);
    console.log('🔍 Raw localStorage data for pending test:', data);

    if (!data) {
      console.log('⚠️ No pending test in localStorage');
      return null;
    }

    const parsed = JSON.parse(data);
    console.log('📋 Parsed pending test data:', parsed);

    // Validate that testId exists and is a non-empty string
    if (!parsed.testId || typeof parsed.testId !== 'string' || parsed.testId.trim() === '') {
      console.log('❌ Invalid testId, clearing pending test');
      clearPendingTest();
      return null;
    }

    // Check if the pending test is still valid (within 24 hours)
    const ONE_DAY_MS = 24 * 60 * 60 * 1000;
    if (!parsed.savedAt || Date.now() - parsed.savedAt > ONE_DAY_MS) {
      console.log('❌ Pending test expired, clearing');
      clearPendingTest();
      return null;
    }

    console.log('✅ Valid pending test found:', parsed.testId);
    return parsed;
  } catch (error) {
    console.error('Error reading pending test from localStorage:', error);
    clearPendingTest(); // Clear corrupted data
    return null;
  }
};

/**
 * Clear the pending test after it's been taken or no longer needed
 */
export const clearPendingTest = () => {
  if (!isStorageAvailable()) {
    return;
  }
  try {
    localStorage.removeItem(PENDING_TEST_KEY);
  } catch (error) {
    console.error('Error clearing pending test from localStorage:', error);
  }
};

/**
 * Check if there's a pending test
 */
export const hasPendingTest = () => {
  return getPendingTest() !== null;
};

